import React, { useState, useCallback } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Card, 
  CardContent, 
  Fade, 
  Slide,
  Avatar,
  IconButton,
  Divider,
  Alert,
  AlertTitle,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Checkbox,
  FormGroup,
  InputAdornment
} from "@mui/material";
import { 
  PersonAdd, 
  Close, 
  CloudUpload,
  CheckCircle,
  Warning,
  Person,
  Phone,
  AccessTime,
  Favorite,
  LocalHospital,
  Restaurant
} from "@mui/icons-material";
import axios from "axios";
import {
  addDoc,
  collection,
  serverTimestamp,
  query,
  where,
  onSnapshot,
  orderBy
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebaseConfig/firebase";
import { useNavigate } from "react-router-dom";
import uniqid from "uniqid";

function AddNewPt() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [date] = useState(new Date());
  const [opdId, setOpdId] = useState(uniqid());
  const [fName, setFname] = useState("");
  const [gender, setGender] = useState("Female");
  const [phNumber, setPhNumber] = useState("");
  const [phNumberConfirm, setPhNumberConfirm] = useState("");
  const [xrayFolderNumber] = useState("");
  const [age, setAge] = useState("");

  // Today's Vitals & Medical History States
  const [todaysBP, setTodaysBP] = useState("");
  const [eatenSomething, setEatenSomething] = useState("");
  const [breastfeeding, setBreastfeeding] = useState("");
  const [pregnancy, setPregnancy] = useState("");
  const [extraMedication, setExtraMedication] = useState("");
  const [medicalConditions, setMedicalConditions] = useState({
    diabetes: false,
    bp: false,
    heartProblem: false,
    thyroid: false,
    allergy: false,
    hepatitis: false,
    hiv: false,
    bloodThinner: false,
  });

  const handleConditionChange = (condition) => {
    setMedicalConditions((prev) => ({
      ...prev,
      [condition]: !prev[condition]
    }));
  };

  const [phNumberError, setPhNumberError] = useState("");
  const [phNumberConfirmError, setPhNumberConfirmError] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);

  // Duplicate detection states
  const [duplicatePatients, setDuplicatePatients] = useState([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [searchingDuplicates, setSearchingDuplicates] = useState(false);
  const [duplicateCheckType, setDuplicateCheckType] = useState(""); // "phone"

  const genderHandleChange = (event) => {
    setGender(event.target.value);
    event.preventDefault();
  };


  // Search for duplicate patients by phone number
  const searchDuplicatesByPhone = useCallback((phoneNumber) => {
    if (!phoneNumber || phoneNumber.length < 10) {
      if (duplicateCheckType === "phone") {
        setDuplicatePatients([]);
        setShowDuplicateWarning(false);
      }
      return;
    }

    setSearchingDuplicates(true);
    setDuplicateCheckType("phone");

    const patientProfile = collection(db, "patientProfile");
    
    const q = query(
      patientProfile,
      where("phNumber", "==", phoneNumber),
      orderBy("created", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const duplicates = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        duplicates.push({
          id: doc.id,
          name: data.name,
          phNumber: data.phNumber,
          age: data.age,
          gender: data.gender,
          created: data.created,
          photoURL: data.photoURL
        });
      });

      setDuplicatePatients(duplicates);
      setShowDuplicateWarning(duplicates.length > 0);
      setSearchingDuplicates(false);
    });

    // Clean up listener after 5 seconds
    setTimeout(() => {
      unsubscribe();
    }, 5000);
  }, [duplicateCheckType]);

  // Handle name change (no duplicate search)
  const handleNameChange = (e) => {
    const name = e.target.value;
    setFname(name);
  };

  // Handle phone number change with immediate duplicate search for complete numbers
  const handlePhoneChange = (e) => {
    const val = e.target.value;
    setPhNumber(val);
    
    // Validation logic
    if (!/^\d{10}$/.test(val)) {
      setPhNumberError("Must be exactly 10 digits");
      // Clear duplicates if number is invalid
      if (duplicateCheckType === "phNumber") {
        setDuplicatePatients([]);
        setShowDuplicateWarning(false);
      }
    } else {
      setPhNumberError("");
      // Immediately search for duplicates when we have a valid 10-digit number
      searchDuplicatesByPhone(val);
    }

    // Update confirm error if they now mismatch
    if (phNumberConfirm && val !== phNumberConfirm) {
      setPhNumberConfirmError("Numbers do not match");
    } else {
      setPhNumberConfirmError("");
    }

    // Phone search is now immediate for valid numbers
  };

  // Handle proceeding with duplicate patient (start new OPD)
  const proceedWithExistingPatient = (patient) => {
    // Navigate to OPD with existing patient data
    navigate("/opd");
    setShowDuplicateWarning(false);
    handleClose();
  };

  // Handle proceeding as different patient
  const proceedAsDifferentPatient = () => {
    setShowDuplicateWarning(false);
    setDuplicatePatients([]);
  };


  // Advanced image compression function with target size
  const compressImage = (file, targetSizeKB = 250, maxWidth = 600, maxHeight = 600) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = async () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        // Iterative compression to reach target size
        let quality = 0.9;
        let blob;
        let attempts = 0;
        const maxAttempts = 10;
        
        do {
          const currentQuality = quality; // Capture quality value for closure
          blob = await new Promise((blobResolve) => {
            canvas.toBlob(blobResolve, 'image/jpeg', currentQuality);
          });
          
          const sizeKB = blob.size / 1024;
          
          if (sizeKB <= targetSizeKB || attempts >= maxAttempts) {
            break;
          }
          
          // Reduce quality more aggressively if file is still too large
          if (sizeKB > targetSizeKB * 2) {
            quality -= 0.15; // Bigger reduction for very large files
          } else {
            quality -= 0.1; // Smaller reduction as we get closer
          }
          
          // Don't go below minimum quality
          if (quality < 0.3) {
            quality = 0.3;
            break;
          }
          
          attempts++;
        } while (true);
        
        console.log(`AddNewPt - Final compression: ${(blob.size / 1024).toFixed(1)} KB with quality ${quality.toFixed(2)}`);
        resolve(blob);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      
      setCompressing(true);
      try {
        // Show original file size
        const originalSizeKB = (file.size / 1024).toFixed(1);
        console.log(`AddNewPt - Original image size: ${originalSizeKB} KB`);
        
        // Compress the image to target size (250 KB max)
        const compressedBlob = await compressImage(file, 250, 600, 600);
        const compressedSizeKB = (compressedBlob.size / 1024).toFixed(1);
        console.log(`AddNewPt - Compressed image size: ${compressedSizeKB} KB (${((1 - compressedBlob.size / file.size) * 100).toFixed(1)}% reduction)`);
        
        // Create a new File object from the compressed blob
        const compressedFile = new File([compressedBlob], file.name, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
        
        setSelectedImage(compressedFile);
        
        // Create preview from compressed image
        const reader = new FileReader();
        reader.onload = (e) => setImagePreview(e.target.result);
        reader.readAsDataURL(compressedFile);
        
      } catch (error) {
        console.error('Error compressing image:', error);
        alert('Error processing image');
        // Fallback to original file if compression fails
        setSelectedImage(file);
        const reader = new FileReader();
        reader.onload = (e) => setImagePreview(e.target.result);
        reader.readAsDataURL(file);
      } finally {
        setCompressing(false);
      }
    }
  };

  const uploadImageToStorage = async (file) => {
    if (!file) return null;
    
    try {
      const imageRef = ref(storage, `patient-photos/${opdId}-${Date.now()}.jpg`);
      await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(imageRef);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    
    // Reset duplicate detection state
    setDuplicatePatients([]);
    setShowDuplicateWarning(false);
    setSearchingDuplicates(false);
    setDuplicateCheckType("");
    
    setOpen(false);
  };

  const addNewPtDB = async () => {
    // ✅ Final validation before submit
    let hasError = false;

    if (!/^\d{10}$/.test(phNumber)) {
      setPhNumberError("Must be exactly 10 digits");
      hasError = true;
    }

    if (!/^\d{10}$/.test(phNumberConfirm)) {
      setPhNumberConfirmError("Must be exactly 10 digits");
      hasError = true;
    } else if (phNumber !== phNumberConfirm) {
      setPhNumberConfirmError("Numbers do not match");
      hasError = true;
    }

    if (!eatenSomething || eatenSomething.trim() === "") {
      alert("Please select whether the patient has eaten something today (Yes / No).");
      return;
    }

    if (gender === "Female") {
      if (!breastfeeding || breastfeeding.trim() === "") {
        alert("Please select whether the patient is breastfeeding (Yes / No).");
        return;
      }
      if (!pregnancy || pregnancy.trim() === "") {
        alert("Please select whether the patient is pregnant (Yes / No).");
        return;
      }
    }

    // Check for duplicate patients
    if (showDuplicateWarning && duplicatePatients.length > 0) {
      // Scroll to duplicate warning
      const duplicateElement = document.querySelector('[data-duplicate-warning]');
      if (duplicateElement) {
        duplicateElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (hasError) return;

    setUploading(true);

    try {
      // Upload image to Firebase Storage if selected
      let photoURL = null;
      if (selectedImage) {
        photoURL = await uploadImageToStorage(selectedImage);
      }

      const abc = fName + " " + phNumber;
      const newSplit = abc.split(" ");
      const newArray = [fName.toLowerCase()];

      for (let i = 0; i < newSplit.length; i++) {
        for (let y = 1; y < newSplit[i].length + 1; y++) {
          newArray.push(newSplit[i].substring(0, y).toLowerCase());
        }
      }

      let datew = JSON.stringify(date).slice(1, 11);

      const initialVisitRecord = {
        date: datew,
        todaysBP: todaysBP || "",
        eatenSomething: eatenSomething || "Yes",
        breastfeeding: gender === "Female" ? breastfeeding : "N/A",
        pregnancy: gender === "Female" ? pregnancy : "N/A",
        extraMedication: extraMedication || "",
        medicalHistory: medicalConditions,
        created: new Date().toISOString()
      };

      const profileData = {
        name: fName,
        namearray: newArray,
        phNumber: phNumber,
        ptId: opdId,
        gender: gender,
        age: age,
        paid: 0,
        photoURL: photoURL,
        todaysBP: todaysBP || "",
        eatenSomething: eatenSomething || "Yes",
        breastfeeding: gender === "Female" ? breastfeeding : "N/A",
        pregnancy: gender === "Female" ? pregnancy : "N/A",
        extraMedication: extraMedication || "",
        medicalHistory: medicalConditions,
        visitHistory: [initialVisitRecord],
        created: serverTimestamp(),
      };

      const patientProfileRef = await addDoc(
        collection(db, "patientProfile"),
        profileData
      );

      const opdData = {
        gender: gender,
        phNumber: phNumber,
        ptId: opdId,
        ptName: fName,
        age: age,
        date: datew,
        ptCreatedDate: datew,
        created: serverTimestamp(),
        originalptId: patientProfileRef.id,
        xrayFolderNumber: xrayFolderNumber || "",
        photoURL: photoURL,
        todaysBP: todaysBP || "",
        eatenSomething: eatenSomething || "Yes",
        breastfeeding: gender === "Female" ? breastfeeding : "N/A",
        pregnancy: gender === "Female" ? pregnancy : "N/A",
        extraMedication: extraMedication || "",
        medicalHistory: medicalConditions,
        nextApptOption: "none",
        nextApptType: "None",
        nextApptWork: "",
        nextApptDate: "",
        nextApptDuration: "",
      };

      await addDoc(collection(db, "opdEntry"), opdData);

      axios
        .post(
          "https://us-central1-patradentalclinic.cloudfunctions.net/expressApi/welcomeMsg",
          {
            name: fName,
            phno: phNumber,
          },
          {
            headers: {
              "Content-type": "application/json; charset=UTF-8",
            },
          }
        )
        .then((res) => {
          console.log(res);
        })
        .catch((err) => {
          console.log(err);
        });

      // Reset form
      setFname("");
      setPhNumber("");
      setPhNumberConfirm("");
      setAge("");
      setGender("Female");
      setOpdId(uniqid());
      setPhNumberError("");
      setPhNumberConfirmError("");
      setSelectedImage(null);
      setImagePreview(null);
      setTodaysBP("");
      setEatenSomething("");
      setBreastfeeding("");
      setPregnancy("");
      setExtraMedication("");
      setMedicalConditions({
        diabetes: false,
        bp: false,
        heartProblem: false,
        thyroid: false,
        allergy: false,
        hepatitis: false,
        hiv: false,
        bloodThinner: false,
      });
      
      // Reset duplicate detection state
      setDuplicatePatients([]);
      setShowDuplicateWarning(false);
      setSearchingDuplicates(false);
      setDuplicateCheckType("");
      
      handleClose();
      navigate("/opd");
    } catch (error) {
      console.error("Error adding patient:", error);
      alert("Error adding patient. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Button
        onClick={handleClickOpen}
        variant="contained"
        startIcon={<PersonAdd sx={{ fontSize: "1.2rem !important" }} />}
        sx={{
          width: "100%",
          py: { xs: 1.2, sm: 1.4 },
          fontSize: { xs: "14px", sm: "15px" },
          fontWeight: 700,
          textTransform: "none",
          borderRadius: "24px",
          background: "linear-gradient(135deg, rgb(5, 2, 43) 0%, rgb(20, 30, 80) 100%)",
          boxShadow: "0 4px 16px rgba(5, 2, 43, 0.25)",
          transition: "all 0.25s ease-in-out",
          "&:hover": {
            background: "linear-gradient(135deg, rgb(10, 5, 60) 0%, rgb(30, 42, 100))",
            boxShadow: "0 6px 20px rgba(5, 2, 43, 0.35)",
            transform: "translateY(-1px)",
          },
        }}
      >
        + Add New Patient
      </Button>

      <Dialog 
        fullWidth 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "20px",
            boxShadow: "0 24px 48px rgba(5, 2, 43, 0.25)",
            overflow: "hidden",
            width: { xs: "94%", sm: "520px" },
            maxWidth: "520px",
            maxHeight: "90vh",
            m: "auto"
          }
        }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
      >
        {/* Modern Dark Header */}
        <DialogTitle 
          sx={{ 
            background: "linear-gradient(135deg, rgb(5, 2, 43) 0%, rgb(20, 30, 80) 100%)",
            color: "white",
            py: 2.5,
            px: 3,
            position: "relative"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{
              bgcolor: "rgba(255,255,255,0.12)",
              p: 1,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center"
            }}>
              <PersonAdd sx={{ fontSize: 24, color: "#90caf9" }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                Add New Patient
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}>
                Fill details to register patient profile
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: "white",
              backgroundColor: "rgba(255,255,255,0.1)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" }
            }}
          >
            <Close sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </DialogTitle>
        
        {/* Content Body */}
        <DialogContent sx={{ p: 3, bgcolor: "#f8fafc", overflowY: "auto" }}>
          <Fade in timeout={400}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>

              {/* Basic Details Container */}
              <Box sx={{
                bgcolor: "#ffffff",
                p: 2.5,
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                display: "flex",
                flexDirection: "column",
                gap: 2
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b", fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Patient Identity
                </Typography>

                <TextField
                  autoFocus
                  fullWidth
                  label="Patient Full Name"
                  placeholder="Enter full name"
                  value={fName}
                  onChange={handleNameChange}
                  size="small"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      bgcolor: "#f8fafc",
                      "& fieldset": { borderColor: "#cbd5e1" },
                      "&:hover fieldset": { borderColor: "rgb(5, 2, 43)" },
                      "&.Mui-focused fieldset": { borderColor: "rgb(5, 2, 43)", borderWidth: 2 }
                    }
                  }}
                />

                {/* WhatsApp Phone */}
                <TextField
                  fullWidth
                  label="WhatsApp Phone Number"
                  placeholder="10 digit mobile number"
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  value={phNumber}
                  error={!!phNumberError}
                  helperText={phNumberError}
                  onChange={handlePhoneChange}
                  size="small"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      bgcolor: "#f8fafc",
                      "& fieldset": { borderColor: "#cbd5e1" },
                      "&:hover fieldset": { borderColor: "rgb(5, 2, 43)" },
                      "&.Mui-focused fieldset": { borderColor: "rgb(5, 2, 43)", borderWidth: 2 }
                    }
                  }}
                />

                {/* Phone search loading */}
                {searchingDuplicates && duplicateCheckType === "phone" && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "rgb(5, 2, 43)", fontSize: "0.8rem", fontWeight: 600 }}>
                    <CircularProgress size={16} />
                    Checking existing records...
                  </Box>
                )}

                {/* Confirm WhatsApp Phone */}
                <TextField
                  fullWidth
                  label="Confirm WhatsApp Number"
                  placeholder="Re-enter 10 digit number"
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  value={phNumberConfirm}
                  error={!!phNumberConfirmError}
                  helperText={phNumberConfirmError}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPhNumberConfirm(val);
                    if (!/^\d{10}$/.test(val)) {
                      setPhNumberConfirmError("Must be exactly 10 digits");
                    } else if (val !== phNumber) {
                      setPhNumberConfirmError("Numbers do not match");
                    } else {
                      setPhNumberConfirmError("");
                    }
                  }}
                  size="small"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      bgcolor: "#f8fafc",
                      "& fieldset": { borderColor: "#cbd5e1" },
                      "&:hover fieldset": { borderColor: "rgb(5, 2, 43)" },
                      "&.Mui-focused fieldset": { borderColor: "rgb(5, 2, 43)", borderWidth: 2 }
                    }
                  }}
                />
              </Box>

              {/* Age & Gender Container */}
              <Box sx={{
                bgcolor: "#ffffff",
                p: 2.5,
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                display: "flex",
                flexDirection: "column",
                gap: 2
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b", fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Age & Gender
                </Typography>

                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                  <Box sx={{ flex: 1, minWidth: "120px" }}>
                    <TextField
                      fullWidth
                      label="Age (Years)"
                      placeholder="e.g. 28"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      size="small"
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          bgcolor: "#f8fafc",
                          "& fieldset": { borderColor: "#cbd5e1" },
                          "&:hover fieldset": { borderColor: "rgb(5, 2, 43)" },
                          "&.Mui-focused fieldset": { borderColor: "rgb(5, 2, 43)", borderWidth: 2 }
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: "180px" }}>
                    <RadioGroup
                      row
                      name="gender"
                      value={gender}
                      onChange={genderHandleChange}
                      sx={{ gap: 1 }}
                    >
                      <FormControlLabel 
                        value="Male" 
                        control={<Radio size="small" sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />} 
                        label={<Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.875rem" }}>Male</Typography>} 
                      />
                      <FormControlLabel
                        value="Female"
                        control={<Radio size="small" sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />}
                        label={<Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.875rem" }}>Female</Typography>}
                      />
                    </RadioGroup>
                  </Box>
                </Box>
              </Box>

              {/* Today's OPD Details & Vitals */}
              <Box sx={{
                bgcolor: "#ffffff",
                p: 2.5,
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                display: "flex",
                flexDirection: "column",
                gap: 2
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b", fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Today's Vitals & Status
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {/* Today's BP */}
                  <TextField
                    fullWidth
                    label="Today's Blood Pressure (BP)"
                    placeholder="e.g. 120/80"
                    value={todaysBP}
                    onChange={(e) => setTodaysBP(e.target.value)}
                    size="small"
                    variant="outlined"
                    helperText="Systolic / Diastolic (e.g. 120/80)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Favorite sx={{ color: "#d32f2f", fontSize: "1.1rem" }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b" }}>
                            mmHg
                          </Typography>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        bgcolor: "#f8fafc",
                        "& fieldset": { borderColor: "#cbd5e1" },
                        "&:hover fieldset": { borderColor: "rgb(5, 2, 43)" },
                        "&.Mui-focused fieldset": { borderColor: "rgb(5, 2, 43)", borderWidth: 2 }
                      }
                    }}
                  />

                  {/* Eaten Something Today */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#334155" }}>
                      Eaten Something Today? <span style={{ color: "#d32f2f" }}>*</span>
                    </Typography>
                    <RadioGroup
                      row
                      value={eatenSomething}
                      onChange={(e) => setEatenSomething(e.target.value)}
                    >
                      <FormControlLabel value="Yes" control={<Radio size="small" sx={{ color: "#2e7d32", "&.Mui-checked": { color: "#2e7d32" } }} />} label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Yes</Typography>} />
                      <FormControlLabel value="No" control={<Radio size="small" sx={{ color: "#d32f2f", "&.Mui-checked": { color: "#d32f2f" } }} />} label={<Typography variant="body2" sx={{ fontWeight: 700 }}>No</Typography>} />
                    </RadioGroup>
                  </Box>

                  {/* Breastfeeding & Pregnancy (for Female) */}
                  {gender === "Female" && (
                    <>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#334155" }}>
                          Breastfeeding? <span style={{ color: "#d32f2f" }}>*</span>
                        </Typography>
                        <RadioGroup
                          row
                          value={breastfeeding}
                          onChange={(e) => setBreastfeeding(e.target.value)}
                        >
                          <FormControlLabel value="Yes" control={<Radio size="small" sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />} label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Yes</Typography>} />
                          <FormControlLabel value="No" control={<Radio size="small" sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />} label={<Typography variant="body2" sx={{ fontWeight: 700 }}>No</Typography>} />
                        </RadioGroup>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#334155" }}>
                          Pregnancy? <span style={{ color: "#d32f2f" }}>*</span>
                        </Typography>
                        <RadioGroup
                          row
                          value={pregnancy}
                          onChange={(e) => setPregnancy(e.target.value)}
                        >
                          <FormControlLabel value="Yes" control={<Radio size="small" sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />} label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Yes</Typography>} />
                          <FormControlLabel value="No" control={<Radio size="small" sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />} label={<Typography variant="body2" sx={{ fontWeight: 700 }}>No</Typography>} />
                        </RadioGroup>
                      </Box>
                    </>
                  )}
                  {/* Extra Medication */}
                  <TextField
                    fullWidth
                    label="Extra / Regular Medication"
                    placeholder="e.g. Aspirin, Insulin, Painkillers"
                    value={extraMedication}
                    onChange={(e) => setExtraMedication(e.target.value)}
                    size="small"
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        bgcolor: "#f8fafc",
                        "& fieldset": { borderColor: "#cbd5e1" },
                        "&:hover fieldset": { borderColor: "rgb(5, 2, 43)" },
                        "&.Mui-focused fieldset": { borderColor: "rgb(5, 2, 43)", borderWidth: 2 }
                      }
                    }}
                  />
                </Box>
              </Box>

              {/* Medical History Checklist */}
              <Box sx={{
                bgcolor: "#ffffff",
                p: 2.5,
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                display: "flex",
                flexDirection: "column",
                gap: 1.5
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b", fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Medical History Checklist
                </Typography>

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={medicalConditions.diabetes} onChange={() => handleConditionChange("diabetes")} sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />}
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Diabetes</Typography>}
                  />
                  <FormControlLabel
                    control={<Checkbox size="small" checked={medicalConditions.bp} onChange={() => handleConditionChange("bp")} sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />}
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>BP / Hypertension</Typography>}
                  />
                  <FormControlLabel
                    control={<Checkbox size="small" checked={medicalConditions.heartProblem} onChange={() => handleConditionChange("heartProblem")} sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />}
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Heart Problem</Typography>}
                  />
                  <FormControlLabel
                    control={<Checkbox size="small" checked={medicalConditions.thyroid} onChange={() => handleConditionChange("thyroid")} sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />}
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Thyroid</Typography>}
                  />
                  <FormControlLabel
                    control={<Checkbox size="small" checked={medicalConditions.allergy} onChange={() => handleConditionChange("allergy")} sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />}
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Allergy</Typography>}
                  />
                  <FormControlLabel
                    control={<Checkbox size="small" checked={medicalConditions.hepatitis} onChange={() => handleConditionChange("hepatitis")} sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />}
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Hepatitis</Typography>}
                  />
                  <FormControlLabel
                    control={<Checkbox size="small" checked={medicalConditions.hiv} onChange={() => handleConditionChange("hiv")} sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />}
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>HIV</Typography>}
                  />
                  <FormControlLabel
                    control={<Checkbox size="small" checked={medicalConditions.bloodThinner} onChange={() => handleConditionChange("bloodThinner")} sx={{ color: "#d32f2f", "&.Mui-checked": { color: "#d32f2f" } }} />}
                    label={<Typography variant="body2" sx={{ fontWeight: 700, color: "#b71c1c" }}>Blood Thinner</Typography>}
                  />
                </Box>
              </Box>

              {/* Duplicate Warning Box */}
              {showDuplicateWarning && duplicatePatients.length > 0 && (
                <Fade in timeout={400}>
                  <Box sx={{ p: 2, borderRadius: "14px", bgcolor: "#fff7ed", border: "1.5px solid #fdba74" }}>
                    <Alert severity="warning" icon={<Warning />} sx={{ mb: 1.5, bgcolor: "transparent", p: 0 }}>
                      <AlertTitle sx={{ fontWeight: 800, color: "#c2410c" }}>Existing Patient Found</AlertTitle>
                      A patient with this phone number already exists.
                    </Alert>

                    <List sx={{ bgcolor: "#ffffff", borderRadius: "10px", p: 0.5, border: "1px solid #fed7aa" }}>
                      {duplicatePatients.map((patient) => (
                        <ListItem key={patient.id} disablePadding>
                          <ListItemButton
                            onClick={() => proceedWithExistingPatient(patient)}
                            sx={{ borderRadius: "8px", p: 1 }}
                          >
                            <ListItemAvatar>
                              <Avatar src={patient.photoURL} sx={{ bgcolor: "rgb(5, 2, 43)", width: 36, height: 36 }}>
                                <Person sx={{ fontSize: 20 }} />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{patient.name}</Typography>}
                              secondary={`${patient.age} yrs • ${patient.gender} • ${patient.phNumber}`}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>

                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={proceedAsDifferentPatient}
                      sx={{ mt: 1.5, textTransform: "none", fontWeight: 700, borderRadius: "8px", borderColor: "#c2410c", color: "#c2410c" }}
                    >
                      Continue as Different Patient
                    </Button>
                  </Box>
                </Fade>
              )}

              {/* Photo Upload Section */}
              <Box sx={{
                bgcolor: "#ffffff",
                p: 2.5,
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                display: "flex",
                flexDirection: "column",
                gap: 1.5
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b", fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Patient Photo (Optional)
                </Typography>

                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="photo-upload"
                  type="file"
                  onChange={handleImageChange}
                />

                {!imagePreview ? (
                  <Box 
                    component="label"
                    htmlFor="photo-upload"
                    sx={{
                      p: 2.5,
                      borderRadius: "12px",
                      border: "2px dashed #cbd5e1",
                      backgroundColor: "#f8fafc",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        borderColor: "rgb(5, 2, 43)",
                        backgroundColor: "#f1f5f9"
                      }
                    }}
                  >
                    <CloudUpload sx={{ fontSize: 36, color: "rgb(5, 2, 43)", mb: 0.5 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                      Click to upload photo
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      Auto-compressed (max 250 KB)
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 1 }}>
                    <Avatar src={imagePreview} sx={{ width: 64, height: 64, border: "2px solid rgb(5, 2, 43)" }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#2e7d32", display: "flex", alignItems: "center", gap: 0.5 }}>
                        <CheckCircle sx={{ fontSize: 16 }} /> Photo Uploaded
                      </Typography>
                      <Button
                        size="small"
                        component="label"
                        htmlFor="photo-upload"
                        sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 700, p: 0 }}
                      >
                        Change Photo
                      </Button>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedImage(null);
                        setImagePreview(null);
                      }}
                      sx={{ color: "#d32f2f" }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>

            </Box>
          </Fade>
        </DialogContent>

        <Divider />
        
        {/* Footer Dialog Actions */}
        <DialogActions sx={{ p: 2.5, bgcolor: "#ffffff", display: "flex", gap: 1.5 }}>
          <Button 
            onClick={handleClose} 
            disabled={uploading}
            variant="outlined"
            sx={{
              flex: 1,
              py: 1,
              borderRadius: "10px",
              borderColor: "#cbd5e1",
              color: "#64748b",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { borderColor: "#d32f2f", color: "#d32f2f", bgcolor: "#ffebee" }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={addNewPtDB}
            disabled={uploading || compressing}
            variant="contained"
            startIcon={
              uploading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PersonAdd sx={{ fontSize: "1.1rem !important" }} />
              )
            }
            sx={{
              flex: 2,
              py: 1,
              borderRadius: "10px",
              fontWeight: 800,
              textTransform: "none",
              background: "linear-gradient(135deg, rgb(5, 2, 43) 0%, rgb(20, 30, 80) 100%)",
              boxShadow: "0 4px 15px rgba(5, 2, 43, 0.25)",
              "&:hover": {
                background: "linear-gradient(135deg, rgb(10, 5, 60) 0%, rgb(30, 42, 100))",
                transform: "scale(1.01)"
              }
            }}
          >
            {uploading ? "Saving..." : "Save Patient"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AddNewPt;
